import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Mail, Phone, Users, Heart, Briefcase } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const JoinOurTeam = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Join Our Team',
      subtitle: 'Build Your Career with Palerm Insurance Agency',
      intro: 'We are looking for passionate insurance professionals who want to make a difference in their community.',
      whyJoin: 'Why Join Palerm Insurance Agency?',
      reasons: [
        {
          icon: Users,
          title: 'Personalized Attention',
          description: 'We provide dedicated support and mentorship to help you grow in your insurance career.'
        },
        {
          icon: Heart,
          title: 'Community Service',
          description: 'Join a team committed to serving diverse communities with integrity and care.'
        },
        {
          icon: Briefcase,
          title: 'Professional Growth',
          description: 'Develop your skills and expertise in a supportive, collaborative environment.'
        }
      ],
      callToAction: 'Interested in Opportunities?',
      callToActionText: 'Contact us to learn more about current opportunities and requirements. We look forward to hearing from you.',
      contactButton: 'Contact Us',
      phoneLabel: 'Office Phone',
      emailSubject: 'Interest in joining Palerm team',
      note: 'Note: Contact us directly to discuss available opportunities, requirements, and details specific to your interest.'
    },
    es: {
      title: 'Únete a nuestro equipo',
      subtitle: 'Construye tu carrera con Palerm Insurance Agency',
      intro: 'Buscamos profesionales de seguros apasionados que quieran marcar la diferencia en su comunidad.',
      whyJoin: '¿Por qué unirse a Palerm Insurance Agency?',
      reasons: [
        {
          icon: Users,
          title: 'Atención Personalizada',
          description: 'Proporcionamos apoyo dedicado y mentoría para ayudarte a crecer en tu carrera de seguros.'
        },
        {
          icon: Heart,
          title: 'Servicio a la Comunidad',
          description: 'Únete a un equipo comprometido a servir a comunidades diversas con integridad y cuidado.'
        },
        {
          icon: Briefcase,
          title: 'Crecimiento Profesional',
          description: 'Desarrolla tus habilidades y experiencia en un ambiente colaborativo y de apoyo.'
        }
      ],
      callToAction: '¿Interesado en oportunidades?',
      callToActionText: 'Contáctanos para conocer más sobre las oportunidades actuales y requisitos. Esperamos saber de ti.',
      contactButton: 'Contáctanos',
      phoneLabel: 'Teléfono de oficina',
      emailSubject: 'Interés en unirme al equipo Palerm',
      note: 'Nota: Contáctanos directamente para discutir oportunidades disponibles, requisitos y detalles específicos de tu interés.'
    }
  };

  const text = content[language];
  const emailSubject = encodeURIComponent(text.emailSubject);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-700 to-blue-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {text.title}
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-4">
              {text.subtitle}
            </p>
            <p className="text-lg text-blue-50 max-w-3xl mx-auto">
              {text.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
              {text.whyJoin}
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              {text.reasons.map((reason, index) => {
                const Icon = reason.icon;
                return (
                  <Card key={index} className="text-center hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-300">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-4">
                        {reason.title}
                      </h3>
                      <p className="text-gray-600">
                        {reason.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-blue-200 shadow-xl">
              <CardContent className="p-8 md:p-12">
                <div className="text-center mb-8">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {text.callToAction}
                  </h2>
                  <p className="text-lg text-gray-700">
                    {text.callToActionText}
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Email CTA */}
                  <a
                    href={`mailto:palermg@palerminsurance.com?subject=${emailSubject}`}
                    className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-full text-center font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    <span className="flex items-center justify-center gap-3">
                      <Mail className="w-6 h-6" />
                      {text.contactButton}
                    </span>
                  </a>

                  {/* Contact Info */}
                  <div className="grid md:grid-cols-2 gap-4 pt-6 border-t border-gray-200">
                    <div className="text-center md:text-left">
                      <p className="text-sm text-gray-600 mb-2">{text.phoneLabel}</p>
                      <a
                        href="tel:+14694348287"
                        className="text-lg font-semibold text-blue-700 hover:text-blue-800 flex items-center justify-center md:justify-start gap-2"
                      >
                        <Phone className="w-5 h-5" />
                        +1 (469) 434-8287
                      </a>
                    </div>
                    <div className="text-center md:text-right">
                      <p className="text-sm text-gray-600 mb-2">Email</p>
                      <a
                        href={`mailto:palermg@palerminsurance.com?subject=${emailSubject}`}
                        className="text-lg font-semibold text-blue-700 hover:text-blue-800 inline-flex items-center gap-2"
                      >
                        <Mail className="w-5 h-5" />
                        palermg@palerminsurance.com
                      </a>
                    </div>
                  </div>
                </div>

                {/* Note */}
                <div className="mt-8 p-4 bg-blue-100 rounded-lg">
                  <p className="text-sm text-gray-700 text-center">
                    <strong>{language === 'en' ? 'Note:' : 'Nota:'}</strong> {text.note}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JoinOurTeam;
